import React from 'react';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Box, Typography } from '@mui/material';
import { useBrands, Brand } from '../hooks/useBrands';
import { usePillars } from '../hooks/usePillars';
import { useParams } from 'react-router-dom';

interface PillarComparisonSelectorProps {
  selectedBrand: string;
  selectedPillar: string;
  onBrandChange: (brand: string) => void;
  onPillarChange: (pillar: string) => void;
}

const PillarComparisonSelector: React.FC<PillarComparisonSelectorProps> = ({
  selectedBrand,
  selectedPillar,
  onBrandChange,
  onPillarChange,
}) => {
  const { orgId: orgIdParam } = useParams<{ orgId: string }>();
  const orgId = orgIdParam ? parseInt(orgIdParam, 10) : 0;
  
  const { brands, loading: brandsLoading } = useBrands(orgId);
  const { pillars, loading: pillarsLoading } = usePillars(orgId);

  const handleBrandChange = (event: SelectChangeEvent<string>) => {
    onBrandChange(event.target.value);
  };

  const handlePillarChange = (event: SelectChangeEvent<string>) => {
    onPillarChange(event.target.value);
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 160px', minWidth: 0 }}>
        <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, color: 'text.secondary' }}>
          Brand
        </Typography>
        <FormControl fullWidth variant="outlined" size="small">
          <Select
            id="brand-select"
            value={selectedBrand}
            onChange={handleBrandChange}
            displayEmpty
            disabled={brandsLoading}
            sx={{ minWidth: 0, bgcolor: 'background.paper', '& .MuiSelect-select': { py: 1.1 } }}
          >
            <MenuItem value="" disabled>
              <em>Select Brand</em>
            </MenuItem>
            {brandsLoading ? (
              <MenuItem value="">Loading...</MenuItem>
            ) : (
              brands?.map((brand: Brand) => (
                <MenuItem key={brand.id} value={brand.id}>
                  {brand.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 160px', minWidth: 0 }}>
        <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, color: 'text.secondary' }}>
          Pillar
        </Typography>
        <FormControl fullWidth variant="outlined" size="small">
          <Select
            id="pillar-select"
            value={selectedPillar}
            onChange={handlePillarChange}
            displayEmpty
            disabled={pillarsLoading}
            sx={{ minWidth: 0, bgcolor: 'background.paper', '& .MuiSelect-select': { py: 1.1 } }}
          >
            <MenuItem value="" disabled>
              <em>Select Pillar</em>
            </MenuItem>
            {pillarsLoading ? (
              <MenuItem value="">Loading...</MenuItem>
            ) : (
              pillars?.map((pillar: string) => (
                <MenuItem key={pillar} value={pillar}>
                  {pillar}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default PillarComparisonSelector; 